import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlinePaperClip,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../services';
import masterDataService from '../../services/masterDataService';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';

const AttachmentTypes = () => {
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAttachmentType, setEditingAttachmentType] = useState(null);
  const [formData, setFormData] = useState({
    typeName: '',
    allowedExt: '',
    maxSizeMb: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadAttachmentTypes();
    } else {
      setError('Please login to view attachment types');
      setLoading(false);
    }
  }, [tenantId]);

  const loadAttachmentTypes = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getAttachmentTypes(tenantId);
      setAttachmentTypes(data);
    } catch (err) {
      setError('Failed to load attachment types. Please try again.');
      console.error('Error loading attachment types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredAttachmentTypes = attachmentTypes.filter((at) =>
    at.typeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    at.allowedExt?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingAttachmentType(null);
    setFormData({
      typeName: '',
      allowedExt: '',
      maxSizeMb: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (attachmentType) => {
    setEditingAttachmentType(attachmentType);
    setFormData({
      typeName: attachmentType.typeName || '',
      allowedExt: attachmentType.allowedExt || '',
      maxSizeMb: attachmentType.maxSizeMb || 0,
      status: attachmentType.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteAttachmentType(id);
      await loadAttachmentTypes();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete attachment type. Please try again.');
      console.error('Error deleting attachment type:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    const extRegex = /^(\.[a-zA-Z0-9]+)(,\.[a-zA-Z0-9]+)*$/;

    // Type Name validations
    if (!formData.typeName.trim()) {
      errors.typeName = 'Type name is required';
    } else if (formData.typeName.length > 50) {
      errors.typeName = 'Type name must be less than 50 characters';
    } else {
      // Duplicate check
      const isDuplicate = attachmentTypes.some(
        (at) =>
          at.typeName?.toLowerCase() === formData.typeName.trim().toLowerCase() &&
          at.attachmentTypeId !== editingAttachmentType?.attachmentTypeId
      );
      if (isDuplicate) {
        errors.typeName = 'Attachment type name already exists';
      }
    }

    // Allowed Extensions validations
    if (!formData.allowedExt.trim()) {
      errors.allowedExt = 'Allowed extensions are required';
    } else if (!extRegex.test(formData.allowedExt.trim().replace(/\s/g, ''))) {
      errors.allowedExt = 'Invalid format. Use .ext1,.ext2 (e.g., .jpg,.png)';
    }

    // Max Size validations
    const sizeVal = parseFloat(formData.maxSizeMb);
    if (formData.maxSizeMb === '' || formData.maxSizeMb === null || formData.maxSizeMb === undefined) {
      errors.maxSizeMb = 'Max size is required';
    } else if (isNaN(sizeVal)) {
      errors.maxSizeMb = 'Max size must be a number';
    } else if (sizeVal <= 0) {
      errors.maxSizeMb = 'Max size must be greater than 0';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const attachmentTypeData = {
        tenantId: tenantId,
        typeName: formData.typeName.trim(),
        allowedExt: formData.allowedExt.trim(),
        maxSizeMb: parseInt(formData.maxSizeMb) || 0,
        status: formData.status,
      };

      if (editingAttachmentType) {
        attachmentTypeData.attachmentTypeId = editingAttachmentType.attachmentTypeId;
        await masterDataService.updateAttachmentType(editingAttachmentType.attachmentTypeId, attachmentTypeData);
      } else {
        await masterDataService.createAttachmentType(attachmentTypeData);
      }

      await loadAttachmentTypes();
      setShowModal(false);
      setFormData({ typeName: '', allowedExt: '', maxSizeMb: '', status: 'Active' });
      setEditingAttachmentType(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save attachment type. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAttachmentType(null);
    setFormData({ typeName: '', allowedExt: '', maxSizeMb: '', status: 'Active' });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading attachment types..." />;
  }

  return (
    <div className="attachment-types-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Attachment Types</h2>
          <p>Manage file attachment type configurations</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Attachment Type
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search attachment types..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredAttachmentTypes.length} {filteredAttachmentTypes.length === 1 ? 'attachment type' : 'attachment types'}
          </div>
        </div>

        {filteredAttachmentTypes.length === 0 ? (
          <div className="empty-state">
            <HiOutlinePaperClip className="empty-icon" />
            <h3>No attachment types found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first attachment type'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Attachment Type
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type Name</th>
                <th>Allowed Extensions</th>
                <th>Max Size (MB)</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAttachmentTypes.map((at, index) => (
                <tr key={at.attachmentTypeId}>
                  <td>{index + 1}</td>
                  <td className="name-cell">{at.typeName}</td>
                  <td>{at.allowedExt || '-'}</td>
                  <td>{at.maxSizeMb} MB</td>
                  <td>
                    <span className={`status-badge ${at.status?.toLowerCase()}`}>
                      {at.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    {at.createdAt
                      ? new Date(at.createdAt).toLocaleDateString()
                      : '-'}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(at)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(at)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingAttachmentType ? 'Edit Attachment Type' : 'New Attachment Type'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="attachment-type-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingAttachmentType
                  ? 'Updating...'
                  : 'Creating...'
                : editingAttachmentType
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="attachment-type-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="typeName">
                Type Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="typeName"
                value={formData.typeName}
                onChange={(e) =>
                  setFormData({ ...formData, typeName: e.target.value })
                }
                className={formErrors.typeName ? 'error' : ''}
                placeholder="e.g., Image, Document, Video"
                maxLength={50}
              />
              {formErrors.typeName && (
                <span className="field-error">{formErrors.typeName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="allowedExt">Allowed Extensions</label>
              <input
                type="text"
                id="allowedExt"
                value={formData.allowedExt}
                onChange={(e) =>
                  setFormData({ ...formData, allowedExt: e.target.value })
                }
                placeholder="e.g., .jpg,.jpeg,.png,.gif or .pdf,.doc,.docx"
                maxLength={100}
              />
              <small style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                Comma-separated list of file extensions (e.g., .jpg,.png)
              </small>
              {formErrors.allowedExt && (
                <span className="field-error">{formErrors.allowedExt}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="maxSizeMb">
                Max Size (MB) <span className="required">*</span>
              </label>
              <input
                type="number"
                id="maxSizeMb"
                value={formData.maxSizeMb}
                onChange={(e) =>
                  setFormData({ ...formData, maxSizeMb: e.target.value })
                }
                className={formErrors.maxSizeMb ? 'error' : ''}
                placeholder="e.g., 5"
                min="1"
              />
              {formErrors.maxSizeMb && (
                <span className="field-error">{formErrors.maxSizeMb}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.attachmentTypeId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.typeName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The attachment type will be marked as inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default AttachmentTypes;

